/**
 * Military Aircraft SVG Icons + Country Flag Mapping
 * NATO-style tactical silhouettes viewed from above
 */
import L from 'leaflet';

// Country ISO code mapping for flags
const COUNTRY_FLAGS = {
  'United States': 'us',
  'United Kingdom': 'gb',
  'France': 'fr',
  'Germany': 'de',
  'Italy': 'it',
  'Israel': 'il',
  'Iran': 'ir',
  'Turkey': 'tr',
  'Saudi Arabia': 'sa',
  'UAE': 'ae',
  'Russia': 'ru',
  'China': 'cn',
  'India': 'in',
  'Australia': 'au',
  'Canada': 'ca',
  'Japan': 'jp',
  'South Korea': 'kr',
  'Egypt': 'eg',
  'Qatar': 'qa',
  'Kuwait': 'kw',
  'Bahrain': 'bh',
  'Oman': 'om',
  'Pakistan': 'pk',
  'NATO': 'nato',
};

// Color palette for different aircraft roles
export const AIRCRAFT_COLORS = {
  fighter: '#00ff88',
  bomber: '#ff4444',
  tanker: '#66ccff',
  transport: '#aaaaff',
  isr: '#ff3366',
  sigint: '#ff3366',
  awacs: '#ffaa00',
  drone: '#ff66ff',
  helicopter: '#88ddaa',
  patrol: '#44aacc',
  vip: '#ffd700',
  default: '#00bbff',
};

// SVG paths for different aircraft types (viewed from above, pointing UP)
const AIRCRAFT_SVGS = {
  fighter: `<path d="M16 1 L17.5 8 L14.5 8 Z M14 8 L18 8 L19 10 L28 16 L27 18 L19 15 L18 22 L21 27 L19 27 L16 24 L13 27 L11 27 L14 22 L13 15 L5 18 L4 16 L13 10 Z" fill="currentColor"/>`,
  bomber: `<path d="M16 1 L18 6 L18 10 L29 16 L29 19 L18 16 L18 22 L22 27 L22 29 L16 26 L10 29 L10 27 L14 22 L14 16 L3 19 L3 16 L14 10 L14 6 Z" fill="currentColor"/>`,
  tanker: `<path d="M16 2 L18 7 L18 10 L28 15 L28 17 L18 15 L18 24 L22 28 L21 29 L16 26 L11 29 L10 28 L14 24 L14 15 L4 17 L4 15 L14 10 L14 7 Z" fill="currentColor"/>`,
  transport: `<path d="M16 2 L18 8 L18 11 L27 16 L27 18 L18 15 L18 24 L22 28 L21 29 L16 26 L11 29 L10 28 L14 24 L14 15 L5 18 L5 16 L14 11 L14 8 Z" fill="currentColor"/>`,
  drone: `<path d="M16 3 L17 10 L15 10 Z M14 10 L18 10 L18 11 L30 15 L30 17 L18 14 L18 26 L20 28 L18 28 L16 26 L14 28 L12 28 L14 26 L14 14 L2 17 L2 15 L14 11 Z" fill="currentColor"/>`,
  helicopter: `<circle cx="16" cy="16" r="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="16" y1="4" x2="16" y2="12" stroke="currentColor" stroke-width="1.5"/>
    <line x1="8" y1="4" x2="24" y2="4" stroke="currentColor" stroke-width="2"/>
    <line x1="16" y1="20" x2="16" y2="28" stroke="currentColor" stroke-width="1.5"/>
    <line x1="12" y1="28" x2="20" y2="28" stroke="currentColor" stroke-width="1.5"/>
    <line x1="13" y1="14" x2="8" y2="12" stroke="currentColor" stroke-width="1.5"/>
    <line x1="19" y1="14" x2="24" y2="12" stroke="currentColor" stroke-width="1.5"/>`,
  awacs: `<path d="M16 4 L18 9 L18 11 L26 15 L26 17 L18 14 L18 24 L21 27 L20 28 L16 26 L12 28 L11 27 L14 24 L14 14 L6 17 L6 15 L14 11 L14 9 Z" fill="currentColor"/>
    <ellipse cx="16" cy="11" rx="6" ry="2" fill="none" stroke="currentColor" stroke-width="1.5"/>`,
  patrol: `<path d="M16 2 L18 8 L18 11 L27 15 L27 17 L18 14 L18 24 L21 28 L20 29 L16 26 L12 29 L11 28 L14 24 L14 14 L5 17 L5 15 L14 11 L14 8 Z" fill="currentColor"/>`,
};

/**
 * Get the aircraft category from the type string
 */
export function getAircraftCategory(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('fighter') || t.includes('f-15') || t.includes('f-16') || t.includes('f-18') ||
      t.includes('f-22') || t.includes('f-35') || t.includes('fast mover') || t.includes('nato'))
    return 'fighter';
  if (t.includes('bomber') || t.includes('b-52') || t.includes('b-1') || t.includes('b-2'))
    return 'bomber';
  if (t.includes('tanker'))
    return 'tanker';
  if (t.includes('transport') || t.includes('airlift') || t.includes('c-17') || t.includes('c-5'))
    return 'transport';
  if (t.includes('drone') || t.includes('uav') || t.includes('rq-4') || t.includes('global hawk'))
    return 'drone';
  if (t.includes('isr') || t.includes('sigint') || t.includes('elint') || t.includes('rc-135'))
    return 'isr';
  if (t.includes('awacs') || t.includes('e-3') || t.includes('tacamo'))
    return 'awacs';
  if (t.includes('helicopter') || t.includes('tiltrotor') || t.includes('v-22'))
    return 'helicopter';
  if (t.includes('patrol') || t.includes('p-8') || t.includes('p-3'))
    return 'patrol';
  if (t.includes('vip') || t.includes('government'))
    return 'vip';
  return 'default';
}

/**
 * Get the color for an aircraft based on its type
 */
export function getAircraftColor(type) {
  const cat = getAircraftCategory(type);
  return AIRCRAFT_COLORS[cat] || AIRCRAFT_COLORS.default;
}

/**
 * Get the flag URL for a country
 */
export function getFlagUrl(origin) {
  const code = COUNTRY_FLAGS[origin];
  if (!code) return null;
  return `https://flagcdn.com/w20/${code}.png`;
}

/**
 * Get the country ISO code
 */
export function getCountryCode(origin) {
  return COUNTRY_FLAGS[origin] || null;
}

/**
 * Create a Leaflet divIcon for a military aircraft
 */
export function createMilitaryIcon(flight, size = 28) {
  const category = getAircraftCategory(flight.type);
  const color = AIRCRAFT_COLORS[category] || AIRCRAFT_COLORS.default;
  const heading = flight.heading || 0;
  const svgContent = AIRCRAFT_SVGS[category] || AIRCRAFT_SVGS.fighter;
  const flagCode = COUNTRY_FLAGS[flight.origin];
  const flagHtml = flagCode
    ? `<img src="https://flagcdn.com/w20/${flagCode}.png" style="position:absolute;bottom:-2px;right:-4px;width:14px;height:10px;border-radius:1px;box-shadow:0 0 3px rgba(0,0,0,0.8);" alt="${flight.origin}"/>`
    : '';

  const html = `<div style="position:relative;width:${size}px;height:${size}px;transform:rotate(${heading}deg);filter:drop-shadow(0 0 6px ${color});">
    <svg viewBox="0 0 32 32" width="${size}" height="${size}" style="color:${color};">
      ${svgContent}
    </svg>
    <div style="transform:rotate(-${heading}deg);position:absolute;bottom:-2px;right:-4px;">${flagHtml}</div>
  </div>`;

  return L.divIcon({
    className: 'military-aircraft-marker',
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Legend data for the map
 */
export const LEGEND_ITEMS = [
  { key: 'fighter', label: 'Chasseur', color: AIRCRAFT_COLORS.fighter },
  { key: 'bomber', label: 'Bombardier', color: AIRCRAFT_COLORS.bomber },
  { key: 'tanker', label: 'Ravitailleur', color: AIRCRAFT_COLORS.tanker },
  { key: 'transport', label: 'Transport', color: AIRCRAFT_COLORS.transport },
  { key: 'isr', label: 'ISR / SIGINT', color: AIRCRAFT_COLORS.isr },
  { key: 'drone', label: 'Drone (UAV)', color: AIRCRAFT_COLORS.drone },
  { key: 'awacs', label: 'AWACS / C2', color: AIRCRAFT_COLORS.awacs },
  { key: 'patrol', label: 'Patrouille maritime', color: AIRCRAFT_COLORS.patrol },
  { key: 'helicopter', label: 'Helicoptere', color: AIRCRAFT_COLORS.helicopter },
];
